import type React from 'react';
import { Link } from 'react-router-dom';
import { ClosingBand, RequestAccessButton, TextLink } from '@/components/marketing/Sections';
import { ShowSpine } from '@/components/marketing/ShowSpine';
import { tracks } from '@/content/showSpine';
import { MediaFrame } from '@/components/marketing/Media';
import { media } from '@/content/media';
import { getAdminUrl } from '@/utils/crossAppLinks';

/* ==========================================================================
   HOME. Written for whoever puts the show together, which is the role and not
   the workspace type: the person at a venue who books the room, and the
   artist booking their own shows (docs/plans/MARKETING_SITE.md, personas 1
   and 2). It says what Showliner is in the H1, then follows one show from the
   first text to settling up, told by the clock in the show spine below the
   hero, with the visitor choosing whose side to follow. Everything else on
   the page is short on purpose: the depth lives on the venues and artists
   pages, one click in.
   ========================================================================== */

/**
 * The three screens of one show, fanned out behind the hero on wide screens:
 * the room's TV at the back, the booker's browser in the middle, a fan's
 * phone in front. Decorative; the same screens are captioned in context
 * further down, so everything here is aria-hidden.
 */
const HeroSurfaces: React.FC = () => (
  <div aria-hidden="true" className="hidden lg:block absolute inset-y-0 right-0 w-[56%] z-10">
    {/* The browser overlaps the TV's chat column and the phone its lineup
        strip, so the act's name, the part of the display worth reading, stays
        clear. */}
    <div className="absolute top-[12%] left-[10%] w-[86%]">
      <div className="absolute -inset-10 bg-primary-500/20 blur-3xl" />
      <div className="relative rounded-md bg-grey-900 p-2 shadow-[0_0_90px_rgba(130,75,167,0.35)]">
        <img src={media.displayNowPlaying.src} alt="" className="block w-full rounded-[3px]" />
      </div>
    </div>
    <div className="absolute bottom-[5%] right-[2%] w-[42%] overflow-hidden rounded-md border border-grey-700 bg-grey-900 shadow-2xl">
      <div className="flex items-center gap-1.5 border-b border-grey-700 bg-grey-800 px-3 py-2">
        <span className="h-2 w-2 rounded-full bg-grey-600" />
        <span className="h-2 w-2 rounded-full bg-grey-600" />
        <span className="h-2 w-2 rounded-full bg-grey-600" />
      </div>
      <img src={media.adminEventDetail.src} alt="" className="block w-full" />
    </div>
    <div className="absolute bottom-[3%] left-[4%] w-[12%] overflow-hidden rounded-[0.9rem] border-[3px] border-grey-700 bg-grey-900 shadow-2xl">
      <img src={media.phoneRoom.src} alt="" className="block w-full" />
    </div>
  </div>
);

const Home: React.FC = () => (
  <>
    {/* ── Hero ── */}
    <section className="relative overflow-hidden lg:min-h-[38rem]">
      <div
        aria-hidden="true"
        className="hidden lg:block absolute inset-y-0 right-0 w-[56%] bg-ink"
        style={{ clipPath: 'polygon(12% 0%, 100% 0%, 100% 100%, 0% 100%)' }}
      />
      <HeroSurfaces />

      <div className="relative z-20 mx-auto max-w-6xl px-4 sm:px-8 section lg:flex lg:min-h-[38rem] lg:items-center">
        <div className="lg:max-w-[40%]">
          <h1 className="font-display text-[clamp(2.25rem,3.6vw,3.25rem)] leading-[1] tracking-tight text-ink text-balance">
            {/* U+2011, a non-breaking hyphen: "show-" / "night" across two
                lines reads as two words. */}
            Booking and show{'‑'}night software for independent venues and artists
          </h1>
          <p className="mt-6 text-lg text-grey-800 leading-relaxed max-w-[40ch]">
            Book the show without the group text, then run the night from your phone. Nobody you
            book needs an account to answer.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
            <RequestAccessButton />
            <TextLink to="#how-it-works">How a show goes ↓</TextLink>
          </div>

          {/* Below lg the fan of screens is hidden, so carry the TV alone. */}
          <MediaFrame
            className="mt-12 lg:hidden"
            media={media.displayNowPlaying}
            frame="tv"
            caption="The room's display, mid-set"
            eager
          />
        </div>
      </div>
    </section>

    <ShowSpine heading="Follow one show, from the first message to settling up" tracks={tracks} />

    {/* ── The honest part, and the price ── */}
    <section className="border-t border-grey-200">
      <div className="mx-auto max-w-6xl px-4 sm:px-8 section-tight grid grid-cols-1 gap-y-10 lg:grid-cols-12 lg:gap-x-12">
        <div className="lg:col-span-7">
          <h2 className="font-display text-2xl sm:text-3xl text-ink leading-tight tracking-tight">What it doesn't do</h2>
          <ul className="mt-4 space-y-2 text-base text-grey-800 leading-relaxed">
            <li>It doesn't sell tickets.</li>
            <li>It doesn't move money between the people on a show. It records what was owed and paid.</li>
            <li>Fans don't download an app.</li>
            <li>It isn't built for arenas.</li>
          </ul>
        </div>
        <div className="lg:col-span-5">
          <h2 className="font-display text-2xl sm:text-3xl text-ink leading-tight tracking-tight">What it costs</h2>
          <p className="mt-4 text-base text-grey-800 leading-relaxed">
            Free for a few shows a month. Venue Pro is $12 a month, Performer Pro is $7, and anyone
            you book answers for free.
          </p>
          <div className="mt-4">
            <TextLink to="/pricing">See the plans →</TextLink>
          </div>
        </div>
      </div>
    </section>

    {/* ── Where to from here ── */}
    <section className="border-t border-grey-200">
      <div className="mx-auto max-w-6xl px-4 sm:px-8 section-tight">
        <ul className="divide-y divide-grey-200 border-y border-grey-200">
          {[
            { to: '/for-venues', label: 'You book shows at a venue, or you put on nights' },
            { to: '/for-performers', label: 'You book your own shows' },
            { to: '/for-performers#a-link', label: 'Somebody sent you a Showliner link' },
          ].map((row) => (
            <li key={row.to}>
              <Link to={row.to} className="group flex items-baseline justify-between gap-6 py-6">
                <span className="font-display text-2xl sm:text-3xl text-ink tracking-tight">{row.label}</span>
                <span className="text-grey-500 group-hover:text-primary-600 transition-colors" aria-hidden="true">→</span>
              </Link>
            </li>
          ))}
        </ul>
        <p className="mt-6 text-sm text-grey-600">
          At a show right now? Scan the code on the screen. Fans can also{' '}
          <a
            href={getAdminUrl('/register?fan=1')}
            className="text-ink underline underline-offset-4 decoration-grey-300 hover:decoration-primary-500 transition-colors"
          >
            make a free account
          </a>{' '}
          to keep what they earn between shows.
        </p>
      </div>
    </section>

    <ClosingBand title="Want to try it?">
      <p>
        We're opening Showliner a few workspaces at a time while it's early. Tell us about your room
        or your band and we'll get you set up.
      </p>
    </ClosingBand>
  </>
);

export default Home;
